import { isNegative, normalizeVoiceText, parseVoiceNumber } from "../../voice/ownerVoiceParsers";
import {
  OWNER_JARVIS_OPERATION_KIND,
  defineOwnerJarvisCapability,
} from "../ownerJarvisCapabilityContract";
import { ownerJarvisApiMethodIsSensitive } from "../ownerJarvisOperationPolicy";
import {
  chooseEntity,
  currencyFromVoice,
  idOf,
  includesAny,
  spokenDate,
  spokenMoney,
} from "../ownerJarvisUtils";
import { reviewOwnerJarvisIncomeCategory } from "./ownerJarvisFinanceCapabilities";

const SENSITIVE = OWNER_JARVIS_OPERATION_KIND.SENSITIVE_WRITE;

function assertSensitiveBackendMethods(methods) {
  methods.forEach((method) => {
    if (!ownerJarvisApiMethodIsSensitive(method)) {
      throw new Error(`La operación Jarvis esperaba un método backend sensible y encontró ${method}.`);
    }
  });
  return methods;
}

function paymentCount(flow) {
  return Number(flow?.transactionIds?.length || 0);
}

function cleanProjectQuery(raw = "") {
  return normalizeVoiceText(raw)
    .replace(/\b(?:si|sí|ya|esta|está|creado|existe|se llama|proyecto|el|la)\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

async function resolveProject(ownerApi, query) {
  const response = await ownerApi.listInitiatives({ status: "", limit: 120 });
  return chooseEntity(response?.initiatives || [], query, ["name", "outcome", "notes"]);
}

async function linkPaymentsAndResume({ ownerApi, navigate, flowRef, flow, initiativeId, initiativeName, shortMessage = false }) {
  await ownerApi.executeSensitive("bulkLinkProjectPayments", { transactionIds: flow.transactionIds, initiativeId });
  const agreementResponse = await ownerApi.projectAgreement(initiativeId);
  const baseTerm = (agreementResponse?.agreement?.terms || []).find((row) => row.kind === "base" && row.status !== "cancelled");
  flowRef.current = baseTerm
    ? { ...flow, stage: "payment-attribution", initiativeId, initiativeName, baseTermId: idOf(baseTerm), baseTermTitle: baseTerm.title }
    : { ...flow, stage: "contract-question", initiativeId, initiativeName };
  navigate(`/dashboard/owner/proyectos/${encodeURIComponent(initiativeId)}`);
  if (shortMessage) {
    return { handled: true, message: baseTerm
      ? `Vinculé los cobros a ${initiativeName}. ¿Corresponden a ${baseTerm.title || "el contrato base"} o a un bloque nuevo?`
      : `Vinculé los cobros a ${initiativeName}. ¿Querés configurar el contrato base ahora?` };
  }
  return { handled: true, message: baseTerm
    ? `Listo. Vinculé ${paymentCount(flow)} cobros a ${initiativeName}. Este proyecto ya tiene ${baseTerm.title || "contrato base"}. ¿Estos cobros corresponden al contrato base o a un bloque o término nuevo?`
    : `Listo. Vinculé ${paymentCount(flow)} cobros a ${initiativeName} sin tocar sus montos ni fechas. ¿Querés que configuremos ahora el contrato base que explica esos pagos?` };
}

export function createOwnerJarvisFinanceSensitiveWriteCapability({ ownerApi, navigate, flowRef }) {
  return defineOwnerJarvisCapability({
    id: "finance-sensitive-writes",
    domain: "finance",
    description: "Ejecuta cambios financieros de alto impacto sólo después de preflight y confirmación explícita.",
    reads: ["owner.finance", "owner.projects"],
    identify({ raw, text }) {
      const flow = flowRef.current;
      if (!flow?.stage) return null;
      if (flow.stage === "category-rename") return { operationId: "rename-category-history" };
      if (flow.stage === "project-exists" && !isNegative(raw)) return { operationId: "link-existing-project" };
      if (flow.stage === "existing-project-name") return { operationId: "link-existing-project" };
      if (flow.stage === "contract-project-name") return { operationId: "link-lot-to-base-contract" };
      if (flow.stage === "monthly-project-name") return { operationId: "link-lot-to-monthly-history" };
      if (flow.stage === "new-project-name") return { operationId: "create-project-and-link-payments" };
      if (flow.stage === "payment-attribution" && includesAny(text, ["contrato base", "base", "contrato existente", "lo de siempre"])) return { operationId: "attribute-payments-to-base" };
      if (flow.stage === "contract-amount") return { operationId: "create-base-contract" };
      if (flow.stage === "term-task") return { operationId: "create-contract-block" };
      return null;
    },
    operations: [
      {
        id: "rename-category-history",
        kind: SENSITIVE,
        backendMethods: ["createFinanceCategory", ...assertSensitiveBackendMethods(["updateFinanceCategory"])],
        invalidates: ["finance"],
        prepare({ input }) {
          const flow = flowRef.current;
          const nextName = String(input?.raw || "").trim().replace(/^jarvis[,:]?\s*/i, "").slice(0, 80);
          if (!nextName || nextName.length < 2) return { ready: false, message: "No tomé bien el nombre. Repetímelo como querés que quede escrito." };
          const selected = flow?.selected || {};
          if (!selected?.name) return { ready: false, message: "Perdí la categoría que estabas corrigiendo. No hice cambios; volvé a elegirla." };
          return {
            payload: { flow, selected, nextName },
            confirmationMessage: `Esto va a renombrar ${selected.name} como ${nextName} también en el historial asociado. ¿Confirmás?`,
          };
        },
        cancel() {
          flowRef.current = null;
          return { message: "Listo, no renombré la categoría ni toqué el historial." };
        },
        async execute({ payload }) {
          const { flow, selected, nextName } = payload;
          if (idOf(selected)) await ownerApi.executeSensitive("updateFinanceCategory", idOf(selected), { name: nextName, kind: selected.kind || "income", renameHistory: true });
          else await ownerApi.createFinanceCategory({ name: nextName, kind: "income", renameFrom: selected.name });
          flowRef.current = null;
          const result = await reviewOwnerJarvisIncomeCategory({ ownerApi, navigate, flowRef, category: nextName, range: flow.range });
          return { ...result, message: `Listo. Renombré ${selected.name} como ${nextName} y retomé lo que estábamos haciendo. ${result.message}` };
        },
        errors: {
          confirmation: "Ese cambio modifica categorización histórica. Necesito un sí explícito antes de hacerlo.",
          cancelled: "Listo, no renombré la categoría ni toqué el historial.",
          execute: "No pude renombrar esa categoría. No voy a asumir que quedó hecha.",
        },
      },
      {
        id: "link-existing-project",
        kind: SENSITIVE,
        backendMethods: assertSensitiveBackendMethods(["bulkLinkProjectPayments"]),
        invalidates: ["projects", "finance"],
        async prepare({ input }) {
          const flow = flowRef.current;
          const query = flow?.stage === "project-exists" ? cleanProjectQuery(input?.raw) : String(input?.raw || "").trim();
          if (!query) return { ready: false, message: "Decime el nombre del proyecto como aparece en Owner 360." };
          const result = await resolveProject(ownerApi, query);
          if (!result.row) {
            if (result.alternatives?.length) return { ready: false, message: `Encontré varios proyectos parecidos: ${result.alternatives.map((row) => row.name).join(", ")}. Decime cuál.` };
            flowRef.current = { ...flow, stage: "existing-project-name" };
            return { ready: false, message: "No lo pude identificar. Decime solamente el nombre del proyecto como aparece en Owner 360." };
          }
          const initiativeId = idOf(result.row);
          return {
            payload: { flow, initiativeId, initiativeName: result.row.name, shortMessage: flow.stage === "existing-project-name" },
            confirmationMessage: `Voy a vincular ${paymentCount(flow)} cobros históricos a ${result.row.name}. Esto cambia su atribución financiera. ¿Confirmás?`,
          };
        },
        cancel({ payload }) {
          if (payload?.flow) flowRef.current = { ...payload.flow, stage: "review" };
          return { message: "Listo, no vinculé ningún cobro al proyecto." };
        },
        async execute({ payload }) {
          return linkPaymentsAndResume({ ownerApi, navigate, flowRef, ...payload });
        },
        errors: {
          confirmation: "Necesito un sí explícito antes de cambiar la atribución de esos cobros.",
          cancelled: "Listo, no vinculé ningún cobro al proyecto.",
          execute: "No pude vincular esos cobros. No hice una atribución parcial a propósito.",
        },
      },
      {
        id: "link-lot-to-base-contract",
        kind: SENSITIVE,
        backendMethods: assertSensitiveBackendMethods(["bulkLinkProjectPayments"]),
        invalidates: ["projects", "finance"],
        async prepare({ input }) {
          const flow = flowRef.current;
          const query = String(input?.raw || "").trim().replace(/^jarvis[,:]?\s*/i, "");
          if (!query) return { ready: false, message: "Decime el nombre del proyecto cuyo contrato querés pagar con este lote." };
          const result = await resolveProject(ownerApi, query);
          if (!result.row) {
            if (result.alternatives?.length) return { ready: false, message: `Encontré varios proyectos parecidos: ${result.alternatives.map((row) => row.name).join(", ")}. Decime cuál.` };
            return { ready: false, message: "No encontré ese proyecto. Decime el nombre como aparece en Owner 360." };
          }
          const initiativeId = idOf(result.row);
          const agreementResponse = await ownerApi.projectAgreement(initiativeId);
          const agreement = agreementResponse?.agreement || null;
          const baseTerm = (agreement?.terms || []).find((row) => row.kind === "base" && row.status !== "cancelled" && row.billingMode !== "monthly");
          if (!baseTerm) {
            return { ready: false, message: `${result.row.name} no tiene un contrato base cerrado para imputar estos pagos. Entrá al proyecto y definí primero el contrato/financiación; la mensualidad queda aparte.` };
          }
          const alreadyOnTarget = Number((agreement?.payments || []).filter((row) => idOf(row.projectTermId) === idOf(baseTerm) && flow.transactionIds?.includes(idOf(row))).length || 0);
          return {
            payload: { flow, initiativeId, initiativeName: result.row.name, baseTermId: idOf(baseTerm), baseTermTitle: baseTerm.title || "Contrato base", alreadyOnTarget },
            confirmationMessage: `Voy a imputar ${paymentCount(flow)} ingresos a ${baseTerm.title || "el contrato base"} de ${result.row.name}. Si alguno estaba vinculado a otro proyecto, contrato o mensualidad manual, esa atribución se reemplaza; la mensualidad futura de WebProfit no se toca. ¿Confirmás?`,
          };
        },
        cancel({ payload }) {
          if (payload?.flow) flowRef.current = { ...payload.flow, stage: "review" };
          return { message: "Listo, no moví esos ingresos ni cambié el contrato." };
        },
        async execute({ payload }) {
          const { flow, initiativeId, initiativeName, baseTermId, baseTermTitle } = payload;
          const response = await ownerApi.executeSensitive("bulkLinkProjectPayments", {
            transactionIds: flow.transactionIds,
            initiativeId,
            projectTermId: baseTermId,
          });
          flowRef.current = null;
          navigate(`/dashboard/owner/proyectos/${encodeURIComponent(initiativeId)}`);

          const base = response?.agreement?.financial?.baseContract || null;
          const financing = base?.financing || null;
          const parts = [`Listo. Imputé ${Number(response?.linkedCount || paymentCount(flow))} ingresos a ${baseTermTitle || "el contrato base"} de ${initiativeName}.`];
          if (Number(response?.movedProjectCount || 0) || Number(response?.movedTermCount || 0)) {
            parts.push("Los que estaban atribuidos en otro lugar dejaron esa atribución anterior; no quedaron duplicados.");
          }
          if (Number(response?.clearedManualRecurringCount || 0)) {
            parts.push(`Saqué ${Number(response.clearedManualRecurringCount)} vínculos manuales viejos de mensualidad para que estos pagos cuenten sólo contra el contrato.`);
          }
          if (base) parts.push(`Del contrato quedan ${spokenMoney(base.outstanding || 0, base.currency || "ARS")}.`);
          if (financing) {
            parts.push(`${Number(financing.paidInstallmentCount || 0)} cuotas están cubiertas y quedan ${Number(financing.pendingInstallmentCount || 0)}.`);
            if (financing.nextInstallment?.dueAt) parts.push(`La próxima cuota queda para ${spokenDate(financing.nextInstallment.dueAt)}.`);
          } else {
            parts.push("Todavía no hay un cronograma de cuotas configurado; el saldo sí quedó actualizado con los pagos reales.");
          }
          parts.push("La mensualidad queda separada y sigue desde su propia fecha de vigencia.");
          return { handled: true, message: parts.join(" ") };
        },
        errors: {
          confirmation: "Necesito un sí explícito porque voy a mover la atribución histórica de esos ingresos al contrato.",
          cancelled: "Listo, no moví esos ingresos ni cambié el contrato.",
          execute: "No pude imputar el lote al contrato. No voy a asumir que quedó conciliado parcialmente.",
        },
      },
      {
        id: "link-lot-to-monthly-history",
        kind: SENSITIVE,
        backendMethods: assertSensitiveBackendMethods(["bulkLinkProjectMonthlyHistory"]),
        invalidates: ["projects", "finance"],
        async prepare({ input }) {
          const flow = flowRef.current;
          const query = String(input?.raw || "").trim().replace(/^jarvis[,:]?\s*/i, "");
          if (!query) return { ready: false, message: "Decime el nombre del proyecto cuya mensualidad histórica querés reconstruir." };
          const result = await resolveProject(ownerApi, query);
          if (!result.row) {
            if (result.alternatives?.length) return { ready: false, message: `Encontré varios proyectos parecidos: ${result.alternatives.map((row) => row.name).join(", ")}. Decime cuál.` };
            return { ready: false, message: "No encontré ese proyecto. Decime el nombre como aparece en Owner 360." };
          }
          const initiativeId = idOf(result.row);
          const agreementResponse = await ownerApi.projectAgreement(initiativeId);
          const agreement = agreementResponse?.agreement || null;
          const primaryTermId = idOf(agreement?.financial?.monthlyAgreement?.termId);
          const monthlyTerms = (agreement?.terms || []).filter((row) => (
            row.status !== "cancelled"
            && row.billingMode === "monthly"
            && !(row.billingSource === "webprofit" && row.webProfitBillingLinked !== true)
          ));
          const monthlyTerm = monthlyTerms.find((row) => idOf(row) === primaryTermId) || monthlyTerms[0] || null;
          if (!monthlyTerm) {
            return { ready: false, message: `${result.row.name} todavía no tiene una mensualidad comercial de Owner. Definila primero en Mensualidad / Billing y después vinculamos este histórico.` };
          }
          return {
            payload: { flow, initiativeId, initiativeName: result.row.name, monthlyTermId: idOf(monthlyTerm), monthlyTermTitle: monthlyTerm.title || "Mensualidad" },
            confirmationMessage: `Voy a tomar ${paymentCount(flow)} ingresos como pagos históricos de ${monthlyTerm.title || "la mensualidad"} de ${result.row.name}. Mantengo monto, moneda, fecha y cuenta reales; si alguno estaba imputado al contrato base u otro término, deja esa atribución. No cambio el precio, la vigencia ni el Billing WebProfit futuro. ¿Confirmás?`,
          };
        },
        cancel({ payload }) {
          if (payload?.flow) flowRef.current = { ...payload.flow, stage: "review" };
          return { message: "Listo, no moví esos ingresos a la mensualidad histórica." };
        },
        async execute({ payload }) {
          const { flow, initiativeId, initiativeName, monthlyTermId, monthlyTermTitle } = payload;
          const response = await ownerApi.executeSensitive("bulkLinkProjectMonthlyHistory", initiativeId, monthlyTermId, {
            transactionIds: flow.transactionIds,
          });
          flowRef.current = null;
          navigate(`/dashboard/owner/proyectos/${encodeURIComponent(initiativeId)}`, { state: { ownerProjectSection: "money", ownerMoneyView: "billing" } });
          const history = response?.history || response?.agreement?.financial?.monthlyAgreement?.history || {};
          const totals = Object.entries(history.byCurrency || {}).map(([currency, amount]) => spokenMoney(amount, currency)).join(" y ");
          const parts = [`Listo. ${Number(response?.linkedCount || paymentCount(flow))} ingresos quedaron como histórico de ${monthlyTermTitle || "la mensualidad"} de ${initiativeName}.`];
          if (history.periodCount) parts.push(`Representan ${Number(history.periodCount)} período${Number(history.periodCount) === 1 ? "" : "s"} histórico${Number(history.periodCount) === 1 ? "" : "s"}${totals ? ` por ${totals}` : ""}.`);
          if (Number(response?.movedTermCount || 0) > 0) parts.push("Los pagos que estaban en el contrato u otro término dejaron esa atribución anterior; no quedaron contados dos veces.");
          parts.push("Los importes históricos quedaron tal como entraron. La mensualidad actual conserva su vigencia y, si está vinculada, WebProfit sigue cobrando hacia adelante con el precio actual.");
          return { handled: true, message: parts.join(" ") };
        },
        errors: {
          confirmation: "Necesito confirmar antes de mover esos ingresos al histórico de la mensualidad.",
          cancelled: "Listo, no moví esos ingresos a la mensualidad histórica.",
          execute: "No pude reconstruir la mensualidad histórica. No asumí que el lote quedó movido parcialmente.",
        },
      },
      {
        id: "create-project-and-link-payments",
        kind: SENSITIVE,
        backendMethods: ["createInitiative", ...assertSensitiveBackendMethods(["bulkLinkProjectPayments"])],
        invalidates: ["today", "projects", "people", "waiting", "finance"],
        prepare({ input }) {
          const flow = flowRef.current;
          const name = String(input?.raw || "").trim().replace(/^jarvis[,:]?\s*/i, "").slice(0, 180);
          if (!name || normalizeVoiceText(name).split(" ").length > 18) return { ready: false, message: "Decime un nombre corto para el proyecto." };
          return {
            payload: { flow, name },
            confirmationMessage: `Voy a crear el proyecto ${name} y vincularle ${paymentCount(flow)} cobros históricos. ¿Confirmás ambas cosas?`,
          };
        },
        cancel({ payload }) {
          if (payload?.flow) flowRef.current = { ...payload.flow, stage: "review" };
          return { message: "Listo, no creé el proyecto ni vinculé los cobros." };
        },
        async execute({ payload }) {
          const { flow, name } = payload;
          const response = await ownerApi.createInitiative({
            name, domain: "freelance", status: "active", priority: "normal",
            outcome: `Proyecto reconstruido desde ${paymentCount(flow)} cobros históricos de ${flow.category}.`,
            startedAt: flow.firstOccurredAt || undefined,
          });
          const initiative = response?.initiative;
          const initiativeId = idOf(initiative);
          await ownerApi.executeSensitive("bulkLinkProjectPayments", { transactionIds: flow.transactionIds, initiativeId });
          flowRef.current = { ...flow, stage: "contract-question", initiativeId, initiativeName: initiative?.name || name };
          navigate(`/dashboard/owner/proyectos/${encodeURIComponent(initiativeId)}`);
          return { handled: true, message: `Creé ${initiative?.name || name} y vinculé ${paymentCount(flow)} cobros históricos. ¿Querés que armemos ahora el contrato base que explica esos pagos?` };
        },
        errors: {
          confirmation: "Necesito un sí explícito porque voy a crear un proyecto y cambiar atribuciones financieras.",
          cancelled: "Listo, no creé el proyecto ni vinculé los cobros.",
          execute: "No pude completar la creación y vinculación del proyecto. Revisá el estado antes de reintentar.",
        },
      },
      {
        id: "attribute-payments-to-base",
        kind: SENSITIVE,
        backendMethods: assertSensitiveBackendMethods(["bulkLinkProjectPayments"]),
        invalidates: ["projects", "finance"],
        prepare() {
          const flow = flowRef.current;
          if (!flow?.initiativeId || !flow?.baseTermId) return { ready: false, message: "Perdí el contrato base que estabas usando. No hice cambios." };
          return {
            payload: { flow },
            confirmationMessage: `Voy a atribuir ${paymentCount(flow)} cobros a ${flow.baseTermTitle || "el contrato base"}. ¿Confirmás?`,
          };
        },
        cancel({ payload }) {
          if (payload?.flow) flowRef.current = { ...payload.flow, stage: "payment-attribution" };
          return { message: "Listo, dejé los cobros sin cambiar de término." };
        },
        async execute({ payload }) {
          const { flow } = payload;
          await ownerApi.executeSensitive("bulkLinkProjectPayments", { transactionIds: flow.transactionIds, initiativeId: flow.initiativeId, projectTermId: flow.baseTermId });
          flowRef.current = null;
          return { handled: true, message: `Perfecto. Estos ${paymentCount(flow)} cobros quedaron atribuidos a ${flow.baseTermTitle || "el contrato base"}. La ficha del proyecto ya los muestra dentro de ese acuerdo.` };
        },
        errors: {
          confirmation: "Necesito confirmar antes de reasignar esos cobros al contrato base.",
          cancelled: "Listo, dejé los cobros sin cambiar de término.",
          execute: "No pude atribuir esos cobros al contrato base.",
        },
      },
      {
        id: "create-base-contract",
        kind: SENSITIVE,
        backendMethods: assertSensitiveBackendMethods(["createProjectTerm"]),
        invalidates: ["projects", "finance"],
        prepare({ input }) {
          const flow = flowRef.current;
          const amount = parseVoiceNumber(input?.raw);
          if (!(amount > 0)) return { ready: false, message: "No pude tomar el monto. Decímelo de nuevo, por ejemplo quinientos mil pesos." };
          const currency = currencyFromVoice(input?.raw);
          return {
            payload: { flow, amount, currency },
            confirmationMessage: `Voy a guardar un contrato base por ${spokenMoney(amount, currency)}${flow.billingMode === "monthly" ? " mensuales" : ""} y asignar los cobros históricos sin término. ¿Confirmás?`,
          };
        },
        cancel({ payload }) {
          if (payload?.flow) flowRef.current = { ...payload.flow, stage: "contract-question" };
          return { message: "Listo, no creé el contrato base." };
        },
        async execute({ payload }) {
          const { flow, amount, currency } = payload;
          const response = await ownerApi.executeSensitive("createProjectTerm", flow.initiativeId, {
            kind: "base", title: "Contrato base", status: "accepted", amount, currency, billingMode: flow.billingMode,
            effectiveFrom: flow.firstOccurredAt || undefined, assignUnassignedPayments: true, source: "voice",
            notes: `Contrato base reconstruido con Jarvis a partir de cobros históricos de ${flow.category}.`,
          });
          const summary = response?.agreement?.summary || {};
          flowRef.current = { ...flow, stage: "more-terms" };
          return { handled: true, message: `Listo. Guardé el contrato base por ${spokenMoney(amount, currency)}${flow.billingMode === "monthly" ? " mensuales" : ""} y sincronicé ${summary.paymentCount || paymentCount(flow)} cobros del proyecto. ¿Hubo después algún aumento, término nuevo o bloque adicional?` };
        },
        errors: {
          confirmation: "Necesito un sí explícito antes de crear un acuerdo económico aceptado y asignarle pagos.",
          cancelled: "Listo, no creé el contrato base.",
          execute: "No pude crear el contrato base. No asumí que los cobros quedaron asignados.",
        },
      },
      {
        id: "create-contract-block",
        kind: SENSITIVE,
        backendMethods: assertSensitiveBackendMethods(["createProjectTerm"]),
        invalidates: ["projects", "finance"],
        prepare({ input }) {
          const flow = flowRef.current;
          const taskTitle = isNegative(input?.raw) ? "" : String(input?.raw || "").trim().replace(/^jarvis[,:]?\s*/i, "").slice(0, 180);
          const amount = Number(flow?.termAmount);
          if (!flow?.initiativeId || !flow?.termTitle || !(amount >= 0) || !flow?.termCurrency) {
            return { ready: false, message: "Perdí parte del bloque que estabas armando. No hice cambios; retomalo desde el proyecto." };
          }
          return {
            payload: { flow, taskTitle },
            confirmationMessage: `Voy a agregar ${flow.termTitle} por ${spokenMoney(amount, flow.termCurrency)}${taskTitle ? ` y crear la tarea ${taskTitle}` : ""}. ¿Confirmás?`,
          };
        },
        cancel({ payload }) {
          if (payload?.flow) flowRef.current = { ...payload.flow, stage: "more-terms" };
          return { message: "Listo, no agregué ese bloque al contrato." };
        },
        async execute({ payload }) {
          const { flow, taskTitle } = payload;
          await ownerApi.executeSensitive("createProjectTerm", flow.initiativeId, {
            kind: "block", title: flow.termTitle, status: "accepted", amount: flow.termAmount, currency: flow.termCurrency, billingMode: "one_time", source: "voice", taskTitle,
            ...(flow.attributeSelectionToNewTerm ? { transactionIds: flow.transactionIds } : {}),
          });
          flowRef.current = null;
          navigate(`/dashboard/owner/proyectos/${encodeURIComponent(flow.initiativeId)}`);
          return { handled: true, message: `Listo. Agregué ${flow.termTitle} por ${spokenMoney(flow.termAmount, flow.termCurrency)}${taskTitle ? ` y creé la tarea ${taskTitle}` : ""}. Todo quedó dentro del mismo proyecto.` };
        },
        errors: {
          confirmation: "Necesito un sí explícito antes de agregar un término económico aceptado al proyecto.",
          cancelled: "Listo, no agregué ese bloque al contrato.",
          execute: "No pude agregar ese bloque al contrato.",
        },
      },
    ],
    errors: {
      prepare: "No pude validar el cambio financiero. No hice modificaciones.",
      unsupported: "Ese cambio financiero todavía no está habilitado para ejecución directa de Jarvis.",
    },
  });
}
